import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@withjoy/joykit';
import { css, keyframes, styled } from '@withjoy/joykit';
import { withWindow } from '@shared/utils/withWindow';

type ProgressiveLoaderProps = Readonly<{
  isLoading: boolean;
  /** Duration (ms) for the indeterminate progress segment (0% → 90%). Default 10_000. */
  animationDurationMs?: number;
  /** Accent color for the bar fill. Defaults to the brand blurple. */
  accentColor?: string;
}>;

type LoaderPhase = Readonly<'idle' | 'loading' | 'completing'>;

const RESET_DELAY_MS = 400;
const DEFAULT_PROGRESSIVE_LOADER_DURATION_MS = 10_000;

const progressAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 90%; }
`;

const StyledLoaderContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 4px;
  background-color: transparent;
  border-radius: 4px;
  overflow: hidden;
`;

const StyledProgressBar = styled(Box)<{ $phase: LoaderPhase; $animationDurationMs: number; $accentColor?: string }>`
  height: 100%;
  background-color: ${props => props.$accentColor ?? props.theme.colors.newUIBrandPrimary};
  width: 0%;
  transition:
    width 0.3s ease-out,
    opacity 0.3s ease-out;

  ${props =>
    props.$phase === 'loading' &&
    css`
      animation: ${progressAnimation} ${props.$animationDurationMs}ms ease-in-out forwards;
    `}

  ${props =>
    props.$phase === 'completing' &&
    css`
      animation: none;
      width: 100%;
      opacity: 0;
    `}
`;

export const ProgressiveLoader = ({ isLoading, animationDurationMs = DEFAULT_PROGRESSIVE_LOADER_DURATION_MS, accentColor }: ProgressiveLoaderProps) => {
  const [phase, setPhase] = useState<LoaderPhase>('idle');
  const wasLoadingRef = useRef(false);

  useEffect(() => {
    let timerId: number | undefined;

    if (isLoading) {
      setPhase('loading');
    } else if (wasLoadingRef.current) {
      setPhase('completing');
      withWindow(global => {
        timerId = global.setTimeout(() => setPhase('idle'), RESET_DELAY_MS);
      });
    }

    wasLoadingRef.current = isLoading;

    return () => {
      if (timerId !== undefined) {
        withWindow(global => global.clearTimeout(timerId));
      }
    };
  }, [isLoading]);

  if (phase === 'idle') {
    return null;
  }

  return (
    <StyledLoaderContainer>
      <StyledProgressBar
        $phase={phase}
        $animationDurationMs={animationDurationMs}
        $accentColor={accentColor}
      />
    </StyledLoaderContainer>
  );
};
